<template>
    <div>
        <rkm-table
            :fields="fields"
            :items="data.form.sinaisVitais"
            hide-filters
            hide-pagination
        >
            <template v-slot:cell(info)="{index, item}">
                <div v-if="item.centroCusto">
                    <rkm-icon
                        :id="`sinal-vital-info-${index}`"
                        class="mr-2"
                        icon="info-circle"
                    />

                    <rkm-popover
                        :target="`sinal-vital-info-${index}`"
                        triggers="hover"
                    >
                        <div>{{item.centroCusto.setor.nome}}</div>
                        <div><rkm-icon icon="user" /> {{item.lotacao.profissional.nome}} - {{item.lotacao.ocupacao.nome}}</div>
                        <div><rkm-icon :icon="['far', 'clock']" /> {{item.dataHora}}</div>
                    </rkm-popover>

                    {{item.dataHora}}
                </div>
            </template>
        </rkm-table>

        <rkm-row class="mt-2">
            <rkm-col cols="6">
                <rkm-form-select
                    :options="anos"
                    label="Ano"
                    v-model="model.ano"
                />
            </rkm-col>

            <rkm-col cols="6">
                <rkm-form-select
                    :options="meses"
                    label="Mês"
                    v-model="model.mes"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row class="mt-2">
            <rkm-col cols="12">
                <rkm-tabs>
                    <rkm-tab title="Pressão arterial">
                        <rkm-chart-scatter
                            :chart-data="chartData"
                            :chart-options="chartOptions"
                            :plugins="plugins"
                        />
                    </rkm-tab>
                </rkm-tabs>
            </rkm-col>
        </rkm-row>

        <rkm-row
            v-if="hasGestacao"
            class="mt-2"
        >
            <rkm-col cols="12">
                <p>A faixa <span class="gestacao">azul</span> representa o(s) período(s) de gestação(ões).</p>
            </rkm-col>
        </rkm-row>
    </div>
</template>

<script>
// Mixins.
import {object} from '@/mixins'

// Others.
import moment from 'moment'

export default {
    computed: {
        anos() {
            const anos = []

            for (let i = 0; i <= 50; i++) {
                const ano = this.others.moment(this.data.additionals.dataAtual, 'DD/MM/YYYY').year() - i
                anos.push({value: ano, text: ano})
            }

            return anos
        },
        meses() {
            const meses = []

            for (let i = 1; i <= 12; i++) {
                meses.push({value: i, text: i})
            }

            return meses
        },
        selectedItemsByAnoMes() {
            return this.clone(this.data.form.sinaisVitais)
                .filter(sinalVital => {
                    const medidoEm = this.others.moment(sinalVital.medidoEm, 'DD/MM/YYYY')

                    return medidoEm.year() === this.model.ano && medidoEm.month() + 1 === this.model.mes
                })
        },
        chartOptions() {
            return this.getScales()
        },
        chartData() {
            const sinaisVitais = this.selectedItemsByAnoMes

            sinaisVitais.sort((a, b) => {
                const aDate = this.others.moment(a.medidoEm, 'DD/MM/YYYY').toDate()
                const bDate = this.others.moment(b.medidoEm, 'DD/MM/YYYY').toDate()

                return aDate - bDate
            })

            return {
                datasets: [
                    {
                        backgroundColor: '#0000FF',
                        borderColor: "#0000FF",
                        borderWidth: 1,
                        data: sinaisVitais.map(sinalVital => {
                            const pressaoSistolica = sinalVital.pressaoArterial ? sinalVital.pressaoArterial.split('/')[0] : null
                            const medidoEm = sinalVital.medidoEm ? this.others.moment(sinalVital.medidoEm, 'DD/MM/YYYY') : null

                            return {
                                x: medidoEm.date(),
                                y: pressaoSistolica,
                            }
                        }),
                        label: 'Pressão sistólica',
                        showLine: true,
                    },
                    {
                        backgroundColor: '#FFA500',
                        borderColor: "#FFA500",
                        borderWidth: 1,
                        data: sinaisVitais.map(sinalVital => {
                            const pressaoDiastolica = sinalVital.pressaoArterial ? sinalVital.pressaoArterial.split('/')[1] : null
                            const medidoEm = sinalVital.medidoEm ? this.others.moment(sinalVital.medidoEm, 'DD/MM/YYYY') : null

                            return {
                                x: medidoEm.date(),
                                y: pressaoDiastolica,
                            }
                        }),
                        label: 'Pressão diastólica',
                        showLine: true,
                    },
                ]
            }
        },
        plugins() {
            return [
                this.getPeriodosGestacaoPlugin(this.data.form.periodosGestacao)
            ]
        },
    },
    created() {
        this.model.ano = this.others.moment(this.data.additionals.dataAtual, 'DD/MM/YYYY').year()
        this.model.mes = this.others.moment(this.data.additionals.dataAtual, 'DD/MM/YYYY').month() + 1
    },
    data: () => ({
        defaultChartOptions: {
            animation: false,
            aspectRatio: 2,
            maintainAspectRatio: false,
            plugins: {
                decimation: {enabled: true},
                legend: {
                    onClick: null,
                },
            },
            scales: {
                y: {
                    min: 50,
                    max: 250,
                    ticks: {
                        stepSize: 10,
                    },
                },
            },
            spanGaps: true,
        },
        fields: [
            {class: 'col-min', key: 'info', label: 'Data / Hora'},
            {key: 'pressaoArterial', label: 'Pressão arterial (mmHg)', sortable: true},
            {key: 'frequenciaCardiaca', label: 'Frequência respiratória (mpm)', sortable: true},
            {key: 'frequenciaCardiaca', label: 'Frequência cardíaca (bpm)', sortable: true},
            {key: 'saturacaoO2', label: 'Saturação de O2 (%)', sortable: true},
            {key: 'temperatura', label: 'Temperatura (°C)', sortable: true},
        ],
        hasGestacao: false,
        model: {
            ano: null,
            mes: null,
        },
        others: {moment},
    }),
    methods: {
        getMinYScale() {
            const sinaisVitais = this.selectedItemsByAnoMes

            let minYScale = Math.min(...sinaisVitais.map(sinalVital => {
                return sinalVital.pressaoArterial ? sinalVital.pressaoArterial.split('/')[0] : null
            })) + this.defaultChartOptions.scales.y.ticks.stepSize

            if (minYScale < 0) {
                minYScale = 0
            }

            return minYScale < this.defaultChartOptions.scales.y.min ? minYScale : this.defaultChartOptions.scales.y.min
        },
        getMaxYScale() {
            const sinaisVitais = this.selectedItemsByAnoMes

            let maxYScale = Math.max(...sinaisVitais.map(sinalVital => {
                return sinalVital.pressaoArterial ? sinalVital.pressaoArterial.split('/')[0] : null
            })) + this.defaultChartOptions.scales.y.ticks.stepSize

            return maxYScale > this.defaultChartOptions.scales.y.max ? maxYScale : this.defaultChartOptions.scales.y.max
        },
        getScales() {
            const y = {
                min: this.getMinYScale(),
                max: this.getMaxYScale(),
                ticks: {
                    autoSkip: false,
                    stepSize: 10,
                },
                title: {
                    display: true,
                    font: {weight: 'bold'},
                    text: 'Pressão (mmHg)',
                },
            }

            return {
                ...this.defaultChartOptions,
                scales: {
                    x: {
                        min: 1,
                        max: this.others.moment(`${this.model.ano}-${this.model.mes}`, 'YYYY-MM').daysInMonth(),
                        ticks: {
                            autoSkip: false,
                            stepSize: 1,
                        },
                        title: {
                            display: true,
                            font: {weight: 'bold'},
                            text: 'Dia',
                        },
                    },
                    y: {
                        ...y,
                        position: 'left',
                    },
                    y2: {
                        ...y,
                        position: 'right',
                    },
                },
            }
        },
        getPeriodosGestacaoPlugin(periodosGestacao) {
            return {
                id: 'periodosGestacao',
                beforeDraw: (chart) => {
                    const {ctx, chartArea: {top}, scales: {x, y}} = chart

                    periodosGestacao.forEach(periodoGestacao => {
                        const inicio = this.others.moment(periodoGestacao.inicio, 'DD/MM/YYYY')
                        const fim = this.others.moment(periodoGestacao.fim, 'DD/MM/YYYY')
                        const isInicioDesteMes = inicio.year() === this.model.ano && inicio.month() + 1 === this.model.mes
                        const isFimDesteMes = fim.year() === this.model.ano && fim.month() + 1 === this.model.mes

                        if (isInicioDesteMes || isFimDesteMes) {
                            let startX = null
                            let endX = null

                            if (isInicioDesteMes) {
                                startX = x.getPixelForValue(inicio.date())
                            } else {
                                startX = x.getPixelForValue(this.others.moment(`${this.model.ano}-${this.model.mes}`, 'YYYY-MM').daysInMonth())
                            }

                            if (isFimDesteMes) {
                                endX = x.getPixelForValue(fim.date())
                            } else {
                                endX = x.getPixelForValue(this.others.moment(`${this.model.ano}-${this.model.mes}`, 'YYYY-MM').daysInMonth())
                            }

                            const startY = y.getPixelForValue(this.getMinYScale())
                            const endY = y.getPixelForValue(this.getMaxYScale())

                            ctx.fillStyle = '#0000FF11'
                            ctx.fillRect(startX, top, endX - startX, startY - endY)

                            this.hasGestacao = true
                        }
                    })
                }
            }
        },
    },
    mixins: [object],
    props: {
        data: {required: true, type: Object},
    },
}
</script>

<style scoped>
.gestacao {
    color: #0000FF;
}
</style>